import React, { useEffect, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';

import { Alerta } from '../../components/alerta';
import CrmMetricCard from '../../components/crmMetricCard';
import DayRangePicker from '../../components/dayRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import {
    formatInteger,
    formatOneDecimal,
    formatPercentage
} from '../../utils/numberUtils';
import ConversaoLPsCompare, { getSlotColor } from './conversaoLPsCompare';
import {
    buildCompareChartRows,
    buildDailyChartRows,
    buildDayKeys,
    buildLandingPages,
    buildLpStats,
    buildRowsSummary,
    buildSummary,
    fetchConversaoLPsPeriods,
    getCountDelta,
    getDefaultCompareIds,
    getRateDelta,
    getSeriesKey,
    getVisibleLandingPages,
    MAX_COMPARED_LPS
} from './conversaoLPsData';
import ConversaoLPsMatrix from './conversaoLPsMatrix';
import ConversaoLPsRanking from './conversaoLPsRanking';
import ConversaoLPsTrend from './conversaoLPsTrend';
import {
    buildDashboardDateRange,
    CRM_DASHBOARD_MIN_DATE
} from './crmDashboardData';
import '../css/crmDashboard.css';
import '../css/crmConversaoLps.css';

const allLpsOption = 'all';
const NOTICE_DURATION_MS = 4000;
const SLOTS = Array.from({ length: MAX_COMPARED_LPS }, (_, slot) => slot);
const POSITIVE_COLOR = 'var(--ok)';
const NEGATIVE_COLOR = 'var(--erro)';

const consolidatedSeriesKeys = { visitas: 'visitas', leads: 'leads', conversao: 'conversao', hasVisits: 'hasVisits' };

const formatRate = (value) => (value === null ? '—' : `${formatPercentage(value)}%`);

const formatSigned = (value) => `${value > 0 ? '+' : ''}${formatOneDecimal(value)}`;

const getDeltaColor = (delta) => {
    if (delta === null || delta === 0) return 'var(--app-muted)';
    return delta > 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;
};

const buildCountDelta = (current, previous) => {
    const delta = getCountDelta(current, previous);

    return {
        helper: getDeltaColor(delta),
        helperText: delta === null ? 'sem base no período anterior' : `${formatSigned(delta)}% vs período anterior`
    };
};

const buildRateDelta = (current, previous, prefix = '') => {
    const delta = getRateDelta(current, previous);
    const deltaText = delta === null ? 'sem base no período anterior' : `${formatSigned(delta)} p.p. vs período anterior`;

    return {
        helper: getDeltaColor(delta),
        helperText: `${prefix}${deltaText}`
    };
};

const ConversaoLPsHero = () => (
    <div className='crm-dashboard-hero'>
        <h1 className='crm-dashboard-title'>Conversão LPs</h1>
        <p className='crm-dashboard-body crm-dashboard-hero-copy'>
            Visitas, leads e conversão de cada landing page.
        </p>
    </div>
);

const ConversaoLPsFilters = ({ dateRange, disabled, lpOptions, onDateRangeApply, onDateRangeReset, onLpChange, selectedIds }) => (
    <Paper className='crm-dashboard-filter-shell crm-lp-filter-shell'>
        <div className='crm-lp-filter-row'>
            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='conversao-lps-filter-label'
                label='Landing pages'
                value={selectedIds}
                options={lpOptions}
                allOptionValue={allLpsOption}
                disabled={disabled}
                onChange={(values) => onLpChange(values.includes(allLpsOption) ? [] : values)}
            />

            <div className='crm-dashboard-range-field'>
                <span className='crm-dashboard-range-label'>Período</span>
                <DayRangePicker
                    ariaLabel='Filtrar conversão das landing pages por período'
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={CRM_DASHBOARD_MIN_DATE}
                    maxDate={new Date()}
                    onApply={onDateRangeApply}
                    onClear={onDateRangeReset}
                />
            </div>
        </div>
    </Paper>
);

const ConversaoLPsSummary = ({ previousSummary, summary }) => {
    const metrics = [
        {
            label: 'Visitas',
            value: formatInteger(summary.visitas),
            accent: 'var(--setta-amber)',
            icon: <VisibilityRoundedIcon sx={{ fontSize: 18 }} />,
            ...buildCountDelta(summary.visitas, previousSummary.visitas)
        },
        {
            label: 'Leads',
            value: formatInteger(summary.leads),
            accent: 'var(--chart-4)',
            icon: <GroupsRoundedIcon sx={{ fontSize: 18 }} />,
            ...buildCountDelta(summary.leads, previousSummary.leads)
        },
        {
            label: 'Conversão',
            value: formatRate(summary.conversao),
            accent: POSITIVE_COLOR,
            icon: <PercentRoundedIcon sx={{ fontSize: 18 }} />,
            ...buildRateDelta(summary.conversao, previousSummary.conversao)
        },
        {
            label: 'Fechamento',
            value: formatRate(summary.fechamento),
            accent: 'var(--app-text)',
            icon: <HowToRegRoundedIcon sx={{ fontSize: 18 }} />,
            ...buildRateDelta(summary.fechamento, previousSummary.fechamento, `${formatInteger(summary.leads_fechados)} fechados · `)
        }
    ];

    return (
        <div className='ui-kpi-grid'>
            {metrics.map((metric) => <CrmMetricCard key={metric.label} {...metric} />)}
        </div>
    );
};

const ConversaoLPs = () => {
    const [periodData, setPeriodData] = useState(null);
    const [dateRange, setDateRange] = useState(buildDashboardDateRange);
    const [selectedIds, setSelectedIds] = useState([]);
    const [viewMode, setViewMode] = useState('consolidado');
    const [metric, setMetric] = useState('visitas');
    const [sortKey, setSortKey] = useState('conversao');
    const [focused, setFocused] = useState([]);
    const [notice, setNotice] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        let ignore = false;

        const loadPeriodData = async () => {
            setIsLoading(true);

            try {
                const nextPeriodData = await fetchConversaoLPsPeriods(dateRange);
                if (ignore) return;

                const availableIds = new Set(nextPeriodData.rows.map((row) => row.lpId));
                setSelectedIds((previousIds) => previousIds.filter((id) => availableIds.has(id)));
                setFocused((previousFocus) => previousFocus.filter((entry) => availableIds.has(entry.id)));
                setPeriodData({ ...nextPeriodData, dateRange });
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadPeriodData();
        return () => { ignore = true; };
    }, [dateRange]);

    useEffect(() => {
        if (!notice) return undefined;

        const timeout = setTimeout(() => setNotice(''), NOTICE_DURATION_MS);
        return () => clearTimeout(timeout);
    }, [notice]);

    const rows = periodData?.rows ?? [];
    const landingPages = buildLandingPages(rows);
    const lpOptions = [
        { value: allLpsOption, label: 'Todas as LPs' },
        ...landingPages.map((landingPage) => ({ value: landingPage.id, label: landingPage.nome }))
    ];
    const visibleLps = getVisibleLandingPages(landingPages, selectedIds);
    const visibleIds = visibleLps.map((landingPage) => landingPage.id);
    const stats = buildLpStats({ landingPages: visibleLps, rows });
    const statsById = new Map(stats.map((stat) => [stat.id, stat]));
    const summary = buildSummary(stats);
    const previousSummary = buildRowsSummary(periodData?.previousRows ?? [], selectedIds);
    const dayKeys = periodData ? buildDayKeys(periodData.dateRange) : [];
    const keptFocus = focused.filter((entry) => statsById.has(entry.id));
    const activeFocus = keptFocus.length > 0
        ? keptFocus
        : getDefaultCompareIds(stats).map((id, slot) => ({ id, slot }));
    const focusedStats = activeFocus.map((entry) => ({ ...statsById.get(entry.id), slot: entry.slot }));
    const focusedSlotById = new Map(activeFocus.map((entry) => [entry.id, entry.slot]));
    const canCompare = visibleLps.length >= 2;
    const isCompareMode = viewMode === 'comparar' && canCompare;

    const handleToggleFocus = (stat) => {
        const isFocused = activeFocus.some((entry) => entry.id === stat.id);

        if (isFocused) {
            if (activeFocus.length === 1) return;
            setFocused(activeFocus.filter((entry) => entry.id !== stat.id));
            return;
        }

        if (activeFocus.length >= MAX_COMPARED_LPS) {
            setNotice(`Máximo de ${MAX_COMPARED_LPS} LPs na comparação. Remova uma para adicionar ${stat.nome}.`);
            return;
        }

        const usedSlots = new Set(activeFocus.map((entry) => entry.slot));
        const freeSlot = SLOTS.find((slot) => !usedSlots.has(slot));
        setFocused([...activeFocus, { id: stat.id, slot: freeSlot }]);
    };

    const handleRemoveFocus = (lpId) => {
        setFocused(activeFocus.filter((entry) => entry.id !== lpId));
    };

    const trendProps = isCompareMode
        ? {
            eyebrow: 'Comparativo diário',
            rows: buildCompareChartRows({ dayKeys, lpIds: focusedStats.map((stat) => stat.id), rows }),
            seriesList: focusedStats.map((stat) => ({
                label: stat.nome,
                color: getSlotColor(stat.slot),
                keys: {
                    visitas: getSeriesKey(stat.id, 'visitas'),
                    leads: getSeriesKey(stat.id, 'leads'),
                    conversao: getSeriesKey(stat.id, 'conversao'),
                    hasVisits: getSeriesKey(stat.id, 'hasVisits')
                }
            }))
        }
        : {
            eyebrow: 'Evolução diária',
            rows: buildDailyChartRows({ dayKeys, lpIds: visibleIds, rows }),
            seriesList: [{ label: 'LPs filtradas', keys: consolidatedSeriesKeys }]
        };

    const renderModeHint = () => {
        if (viewMode !== 'comparar') {
            return <>Soma das LPs no filtro. Use <b>Comparar LPs</b> para ver lado a lado.</>;
        }

        if (!canCompare) {
            return <>Selecione pelo menos <b>2 LPs</b> no filtro para comparar.</>;
        }

        return <>Até <b>{MAX_COMPARED_LPS} LPs</b> com cor fixa por página.</>;
    };

    return (
        <Box className='mainBox crm-dashboard-page crm-lp-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-dashboard-frame'>
                <div className='crm-dashboard-content'>
                    <div className='crm-dashboard-topbar'>
                        <ConversaoLPsHero />
                        <ConversaoLPsFilters
                            dateRange={dateRange}
                            disabled={periodData === null}
                            lpOptions={lpOptions}
                            selectedIds={selectedIds}
                            onDateRangeApply={(startDate, endDate) => setDateRange({ startDate, endDate })}
                            onDateRangeReset={() => setDateRange(buildDashboardDateRange())}
                            onLpChange={setSelectedIds}
                        />
                    </div>

                    {isLoading && <LinearProgress className='crm-dashboard-loading' variant='indeterminate' />}

                    {periodData && landingPages.length === 0 && (
                        <Paper className='crm-lp-panel crm-lp-empty-panel'>
                            <p className='crm-lp-empty'>Nenhuma landing page com visitas no período.</p>
                        </Paper>
                    )}

                    {landingPages.length > 0 && (
                        <div className='crm-lp-content'>
                            <div className='crm-lp-mode'>
                                <div className='crm-lp-segment' role='tablist' aria-label='Modo de visualização'>
                                    <button
                                        type='button'
                                        role='tab'
                                        className={viewMode === 'consolidado' ? 'is-active' : ''}
                                        aria-selected={viewMode === 'consolidado'}
                                        onClick={() => setViewMode('consolidado')}
                                    >
                                        Consolidado
                                    </button>
                                    <button
                                        type='button'
                                        role='tab'
                                        className={viewMode === 'comparar' ? 'is-active' : ''}
                                        aria-selected={viewMode === 'comparar'}
                                        onClick={() => setViewMode('comparar')}
                                    >
                                        Comparar LPs
                                    </button>
                                </div>
                                <span className='crm-lp-hint'>{renderModeHint()}</span>
                            </div>

                            {isCompareMode
                                ? (
                                    <>
                                        <ConversaoLPsCompare
                                            focusedStats={focusedStats}
                                            notice={notice}
                                            onRemove={handleRemoveFocus}
                                        />
                                        <ConversaoLPsTrend metric={metric} onMetricChange={setMetric} {...trendProps} />
                                        <ConversaoLPsRanking
                                            focusedSlotById={focusedSlotById}
                                            sortKey={sortKey}
                                            stats={stats}
                                            summary={summary}
                                            onSortChange={setSortKey}
                                            onToggleFocus={handleToggleFocus}
                                        />
                                    </>
                                )
                                : (
                                    <>
                                        <ConversaoLPsSummary previousSummary={previousSummary} summary={summary} />
                                        <div className='crm-lp-grid'>
                                            <ConversaoLPsRanking
                                                sortKey={sortKey}
                                                stats={stats}
                                                summary={summary}
                                                onSortChange={setSortKey}
                                            />
                                            <ConversaoLPsMatrix stats={stats} />
                                        </div>
                                        <ConversaoLPsTrend metric={metric} onMetricChange={setMetric} {...trendProps} />
                                    </>
                                )}
                        </div>
                    )}
                </div>
            </div>
        </Box>
    );
};

export default ConversaoLPs;
